import { IconGitHub } from "@/assets/icons/geist/IconGitHub";
import { IconGoogle } from "@/assets/icons/geist/IconGoogle";
import { IconKey } from "@/assets/icons/geist/IconKey";
import { Button } from "@/components/flexkit/Button";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import type { PlainLoginProvider } from "@/queries/account-queries";
import { type FC } from "react";
import { IconGoogleColor } from "@/assets/icons/IconGoogleColor";

type Props = {
    onContinueWithSSO: () => void;
    returnTo: string;
    loginProviders?: PlainLoginProvider[];
    allowCustom?: boolean;
    hideLoginToClassic?: boolean;
    newLoginLayout?: boolean;
};
export const DefaultSSOForm: FC<Props & PropsWithClassName> = ({
    onContinueWithSSO,
    className,
    returnTo,
    hideLoginToClassic,
    newLoginLayout,
    loginProviders,
    allowCustom,
}) => {
    const showGithub = loginProviders?.some((p) => p.provider === "github") ?? false;
    const showGoogle = loginProviders?.some((p) => p.provider === "google") ?? false;
    const customSSO = loginProviders?.find((p) => p.provider === "custom");
    const showContinueWithSSO = allowCustom === true;
    const showClassicAlternative = allowCustom === true && !hideLoginToClassic;

    const buildReturnUrl = ({ issuer }: { issuer: string }) => {
        const url = new URL(returnTo, window.location.origin);
        url.searchParams.set("issuer", issuer);
        return encodeURIComponent(url.toString());
    };

    return (
        <div id="default-sso-form" className={cn(className)}>
            {showGithub && (
                <Button
                    size="lg"
                    variant={newLoginLayout ? "outline" : "secondary"}
                    LeadingIcon={IconGitHub}
                    className="w-full"
                    asChild
                >
                    <a
                        id="signin-github"
                        href={`/auth/oidc/start?returnTo=${buildReturnUrl({ issuer: "github.com" })}&issuer=github.com&reuseGithubToken=true`}
                        data-tracking-id="signin-github"
                    >
                        <span className={cn({ "flex-grow text-center": !newLoginLayout })}>Continue with GitHub</span>
                    </a>
                </Button>
            )}
            {showGoogle && (
                <Button size="lg" variant={newLoginLayout ? "outline" : "secondary"} className="w-full" asChild>
                    <a
                        id="signin"
                        href={`/auth/oidc/start?returnTo=${encodeURIComponent(returnTo)}&issuer=https://accounts.google.com`}
                        data-tracking-id="signin-google"
                    >
                        {newLoginLayout ? <IconGoogleColor className="size-6" /> : <IconGoogle size="lg" />}
                        <span className={cn({ "flex-grow text-center": !newLoginLayout })}>Continue with Google</span>
                    </a>
                </Button>
            )}
            {showContinueWithSSO && (
                <Button
                    size="lg"
                    variant={newLoginLayout ? "outline" : "secondary"}
                    LeadingIcon={IconKey}
                    className="w-full"
                    onClick={onContinueWithSSO}
                    data-tracking-id="confirm-default-sso-form"
                >
                    <span className={cn({ "text-center": !newLoginLayout })}>Continue with SSO</span>
                </Button>
            )}
            {customSSO && (
                <a id="signin-custom" href={customSSO.loginUrl} data-tracking-id="signin-custom-sso">
                    <Button size="lg" variant="secondary" LeadingIcon={IconKey} className="w-full">
                        <span className={cn({ "flex-grow text-center": !newLoginLayout })}>Continue with SSO</span>
                    </Button>
                </a>
            )}

            {showClassicAlternative && (
                <Button size="lg" variant="link" className="w-full" asChild>
                    <ExternalLink href="https://gitpod.io/login">Log in to Gitpod Classic</ExternalLink>
                </Button>
            )}
        </div>
    );
};
